import { Badge, Box } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { BADGE, STATUS, SURFACE } from "@/theme/tokens";

/** `control`: inset on a control's corner. `text`: just past the top-right of a line of text, as a superscript. */
export type FilterActiveDotPlacement = "control" | "text";
/** `standard`: a filter's quiet state dot. `large`: a dot that asks for an action, such as saving edits. */
export type FilterActiveDotSize = "standard" | "large";

type Props = {
	/** True while the filter hides something. */
	active: boolean;
	children: ReactNode;
	dataTestId: string;
	/** The dot's hover text and accessible name, already translated, for a dot that flags something other than a filter. */
	label?: string;
	placement?: FilterActiveDotPlacement;
	size?: FilterActiveDotSize;
};

/**
 * An orange dot on a filter control's top-right corner while the filter is narrowed. A closed dropdown shows
 * only a truncated value, so without it nothing says the grid is hiding rows. Also flags other state a control
 * hides the same way (unsaved edits), with its own `label`.
 *
 * A `standard` badge shrunk to a dot rather than `variant="dot"`: the dot variant renders no content, so there is
 * nothing to hang the hover tooltip on. Still MUI's badge, so it keeps its show/hide animation.
 */
export const FilterActiveDot = ({ active, children, dataTestId, label, placement, size }: Props) => {
	const { t } = useTranslation();
	// DEFAULT: without a label the dot says what it was built for, a narrowed filter.
	const name = label ?? t("filter.active.tooltip");
	// DEFAULT: a filter's dot, the size it has always had.
	const diameter = size === "large" ? BADGE.DOT_SIZE_LG : BADGE.DOT_SIZE;
	return (
		<Badge
			invisible={!active}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			data-testid={dataTestId}
			badgeContent={
				<InHouseTooltip title={name}>
					<Box component="span" role="img" aria-label={name} sx={{ display: "block", width: "100%", height: "100%", borderRadius: "50%" }} />
				</InHouseTooltip>
			}
			sx={(theme) => ({
				"& .MuiBadge-badge": {
					// Offset, not a transform override: MUI hides the badge by scaling its transform.
					...(placement === "text"
						? // Past the text's end, level with its cap height, so it never covers the last letter.
							{ top: theme.spacing(0.5), right: theme.spacing(-0.75) }
						: // DEFAULT: a control's corner. Inset so the dot sits on the control rather than off its rounded edge.
							{ top: theme.spacing(0.9), right: theme.spacing(0.9) }),
					width: diameter,
					height: diameter,
					minWidth: diameter,
					padding: 0,
					borderRadius: "50%",
					// Orange, not brand: it has to stand out against the brand-coloured controls around it.
					backgroundColor: STATUS.WARNING.main,
					boxShadow: `0 0 0 2px ${SURFACE}`,
				},
			})}
		>
			{children}
		</Badge>
	);
};
